import React, { useEffect, useMemo, useRef, useState } from 'react'
import { PlusIcon } from 'lucide-react'
import {
  buildPropertyOptions,
  buildProjectOptions,
  filterPropertyProjectOptions,
  findPropertyProjectOption,
  PropertyItem,
  PropertyProjectOption,
  ProjectItem,
} from '../lib/propertyProjectSearch'

export interface PropertyProjectSearchSelectProps {
  mode: 'property' | 'project'
  properties?: PropertyItem[]
  projects?: ProjectItem[]
  value: string | null | undefined
  onChange: (id: string, option: PropertyProjectOption | null) => void
  onCreate?: () => void
  disabled?: boolean
  required?: boolean
  label?: string
  placeholder?: string
  id?: string
}

export function PropertyProjectSearchSelect({
  mode,
  properties = [],
  projects = [],
  value,
  onChange,
  onCreate,
  disabled = false,
  required = false,
  label,
  placeholder,
  id,
}: PropertyProjectSearchSelectProps) {
  const defaultLabel = mode === 'property' ? 'Property' : 'Project'
  const defaultPlaceholder =
    mode === 'property' ? 'Type to search properties...' : 'Type to search projects...'
  const createLabel =
    mode === 'property' ? 'Create new property' : 'Create new project'
  const inputId = id || `${mode}-search-select`

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)

  const allOptions = useMemo(
    () =>
      mode === 'property' ? buildPropertyOptions(properties) : buildProjectOptions(projects),
    [mode, properties, projects]
  )
  const selectedOption = useMemo(
    () => findPropertyProjectOption(allOptions, value),
    [allOptions, value]
  )
  const filteredOptions = useMemo(
    () => filterPropertyProjectOptions(allOptions, query),
    [allOptions, query]
  )
  const hasCreateAction = Boolean(onCreate)
  const totalNavigableItems = filteredOptions.length + (hasCreateAction ? 1 : 0)
  const createActionIndex = filteredOptions.length

  useEffect(() => {
    if (!isOpen) {
      setQuery(selectedOption?.label ?? '')
    }
  }, [selectedOption, isOpen])

  useEffect(() => {
    setHighlightedIndex(0)
  }, [query, isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setQuery(selectedOption?.label ?? '')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [selectedOption])

  const handleSelect = (option: PropertyProjectOption) => {
    onChange(option.id, option)
    setQuery(option.label)
    setIsOpen(false)
  }

  const handleClear = () => {
    onChange('', null)
    setQuery('')
    setIsOpen(false)
    inputRef.current?.focus()
  }

  const handleCreate = () => {
    setIsOpen(false)
    onCreate?.()
  }

  const handleActionAtIndex = (index: number) => {
    if (index < filteredOptions.length) {
      handleSelect(filteredOptions[index])
    } else if (hasCreateAction && index === createActionIndex) {
      handleCreate()
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!isOpen) {
        setIsOpen(true)
        return
      }
      if (totalNavigableItems > 0) {
        setHighlightedIndex((prev) => Math.min(prev + 1, totalNavigableItems - 1))
      }
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlightedIndex((prev) => Math.max(prev - 1, 0))
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      if (isOpen && totalNavigableItems > 0) {
        handleActionAtIndex(highlightedIndex)
      }
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      setIsOpen(false)
      setQuery(selectedOption?.label ?? '')
    }
  }

  const listboxId = `${inputId}-listbox`
  const accentClass =
    mode === 'property' ? 'text-green-700 hover:bg-green-50' : 'text-blue-600 hover:bg-blue-50'
  const highlightCreateClass =
    mode === 'property' ? 'bg-green-50' : 'bg-blue-50'

  return (
    <div ref={containerRef} className="relative">
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-2">
        {label ?? defaultLabel}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          value={query}
          disabled={disabled}
          placeholder={placeholder ?? defaultPlaceholder}
          onChange={(e) => {
            setQuery(e.target.value)
            setIsOpen(true)
            if (!e.target.value.trim() && value) {
              onChange('', null)
            }
          }}
          onFocus={() => {
            if (!disabled) setIsOpen(true)
          }}
          onKeyDown={handleKeyDown}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : ''
          }`}
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
            aria-label="Clear selection"
          >
            ×
          </button>
        )}
      </div>

      {isOpen && !disabled && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg"
        >
          {filteredOptions.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500">No matches found</li>
          ) : (
            filteredOptions.map((option, index) => (
              <li
                key={option.id}
                role="option"
                aria-selected={highlightedIndex === index}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`px-3 py-2 cursor-pointer ${
                  highlightedIndex === index ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <p className="text-sm text-gray-900 truncate">{option.label}</p>
                {option.subtitle && (
                  <p className="text-xs text-gray-500 truncate">{option.subtitle}</p>
                )}
              </li>
            ))
          )}

          {hasCreateAction && (
            <>
              <li className="border-t border-gray-100" role="presentation" />
              <li
                role="option"
                aria-selected={highlightedIndex === createActionIndex}
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleCreate}
                onMouseEnter={() => setHighlightedIndex(createActionIndex)}
                className={`px-3 py-2 cursor-pointer flex items-center gap-2 text-sm font-medium ${accentClass} ${
                  highlightedIndex === createActionIndex ? highlightCreateClass : ''
                }`}
              >
                <PlusIcon className="h-4 w-4 shrink-0" />
                {createLabel}
              </li>
            </>
          )}
        </ul>
      )}
    </div>
  )
}
